import { CsprMarketEntryPoint, Deploy } from '@block-explorer/entities';
import styled from 'styled-components';
import { FlexColumn, FlexRow } from '@make-software/cspr-design';
import { BodyText, Tooltip } from '@block-explorer/ui';
import { SHORTENED_ENTRY_TYPE_LENGTH, truncateName } from '../utils';
import { AccountInfoIcon } from '../../account-info-icon/account-info-icon';
import { ACCOUNT_DETAILS_PATH } from '../../../router/paths';
import React from 'react';
import { csprMarkerMessage } from '../../../services/deploy-action-messages';
import { useAccountInfoContext } from '../../../services/account-data-service';
import { buildRouterPath } from '../../../router/utils';
import { DefaultActionMessage } from './default-action-message';
import { ContractIdentifier } from '../../contract-identifier/contract-identifier';
import { useTranslation } from 'react-i18next';

const EntryTypeRow = styled(FlexRow)(() => ({
  maxWidth: '25rem',
}));
export const CsprMarketActionMessage = ({ deploy }: { deploy: Deploy }) => {
  const { t } = useTranslation();
  const { getPublicKeyAccountHash } = useAccountInfoContext();
  const message = csprMarkerMessage().prepareActionMessageData(
    deploy,
    getPublicKeyAccountHash,
    t
  );

  if (!message) {
    return <DefaultActionMessage deploy={deploy} />;
  }

  const identifierPrefix: Record<CsprMarketEntryPoint, string> = {
    [CsprMarketEntryPoint.delist_token]: 'from',
    [CsprMarketEntryPoint.list_token]: 'on',
    [CsprMarketEntryPoint.accept_offer]: 'on',
    [CsprMarketEntryPoint.cancel_offer]: 'on',
    [CsprMarketEntryPoint.make_offer]: 'on',
    [CsprMarketEntryPoint.buy_token]: 'on',
  };

  const publicKey1WithFallbackToHash =
    message.account1?.publicKey || message.account1?.accountHash;

  const account1Path = publicKey1WithFallbackToHash
    ? buildRouterPath(
        ACCOUNT_DETAILS_PATH,
        { id: publicKey1WithFallbackToHash },
        undefined
      )
    : '';

  return (
    <FlexColumn>
      <EntryTypeRow align={'baseline'} itemsSpacing={4}>
        <FlexRow itemsSpacing={6} align={'baseline'}>
          <Tooltip tooltipContent={message.entryPointName}>
            <BodyText monotype size={3} wordBreak noWrap>
              {truncateName(message.actionName, SHORTENED_ENTRY_TYPE_LENGTH)}
            </BodyText>
          </Tooltip>
          {message.numOfTokens && (
            <>
              <BodyText monotype size={3} wordBreak noWrap>
                {message.numOfTokens}
              </BodyText>
              <BodyText size={3} variation={'darkGray'}>{`NFT${
                message.numOfTokens > 1 ? '(s)' : ''
              }`}</BodyText>
            </>
          )}
        </FlexRow>
        <BodyText size={3} variation={'darkGray'}>
          <FlexRow itemsSpacing={6} align={'center'}>
            {message.account1 && publicKey1WithFallbackToHash && (
              <>
                {message.prefix1 && <span>{message.prefix1}</span>}
                <AccountInfoIcon
                  publicKey={publicKey1WithFallbackToHash}
                  logo={message.account1.logo || null}
                  path={account1Path}
                />
              </>
            )}
            {message.callerAccount && message.callerAccount.publicKey && (
              <>
                {message.callerPrefix && <span>{message.callerPrefix}</span>}
                <AccountInfoIcon
                  publicKey={message.callerAccount.publicKey}
                  logo={null}
                  path={buildRouterPath(
                    ACCOUNT_DETAILS_PATH,
                    { id: message.callerAccount?.publicKey },
                    undefined
                  )}
                />
              </>
            )}
          </FlexRow>
        </BodyText>{' '}
      </EntryTypeRow>
      <FlexRow itemsSpacing={4}>
        <BodyText variation={'darkGray'} size={3}>
          {message.entryPointName
            ? identifierPrefix[message.entryPointName]
            : 'with'}
        </BodyText>
        <ContractIdentifier
          hash={deploy.contractPackage.contract_package_hash}
          contractPackage={deploy.contractPackage}
          hideContractType
        />
      </FlexRow>
    </FlexColumn>
  );
};
